import {useState} from "react";
import {
    Anchor,
    Button,
    Loader,
    ScrollArea,
    Stack,
    Text,
    TextInput,
} from "@mantine/core";
import {useTranslation} from "react-i18next";
import {useNavigate} from "react-router-dom";
import {dibApi} from "../../../chat/dibApi";
import {buildCollectionUrl, type TopicMeta} from "./utils";

const FILLINGS_TO_SHOW = 6;

const dateFormatter = new Intl.DateTimeFormat("nb", {dateStyle: "medium"});
const formatDate = (date: string) => {
    try {
        return dateFormatter.format(new Date(date));
    } catch {
        return date;
    }
};

type OpenFillingModalProps = {
    onClose: () => void;
    onCollectionChange?: () => void;
    topicId: string;
    topicMeta: TopicMeta;
};

export const OpenFillingModal = ({
    onClose,
    onCollectionChange,
    topicId,
    topicMeta,
}: OpenFillingModalProps) => {
    const {t} = useTranslation();
    const navigate = useNavigate();
    const {data: collections = [], isLoading, isError, isSuccess} =
        dibApi.endpoints.getTopicCollections.useQuery(
            {topicId},
            {skip: !topicId},
        );

    const [filter, setFilter] = useState("");
    const [showAll, setShowAll] = useState(false);

    const handleSearch = (value: string) => {
        if (!showAll && value.length > 0) setShowAll(true);
        setFilter(value);
    };

    const lowerFilter = filter.toLowerCase();
    const collectionsToShow = showAll
        ? collections
        : collections.slice(0, FILLINGS_TO_SHOW);
    const filtered = (
        filter
            ? collections.filter(
                  (c) =>
                      c.data.name.toLowerCase().includes(lowerFilter) ||
                      c.data.lastEdit?.toLowerCase().includes(lowerFilter) ||
                      c.data.created?.toLowerCase().includes(lowerFilter),
              )
            : collectionsToShow
    ).filter((c) => c.data.name.length > 0);

    const handleClick = (collectionId: string, collectionName: string) => {
        onCollectionChange?.();
        navigate(
            buildCollectionUrl(topicMeta, {
                name: collectionName,
                id: collectionId,
            }),
        );
        onClose();
    };

    return (
        <Stack gap="sm">
            <TextInput
                placeholder={t("Phrase.SearchFilling")}
                value={filter}
                onChange={(e) => handleSearch(e.currentTarget.value)}
                size="sm"
            />

            {isLoading && <Loader size="sm" mx="auto" />}

            {isError && (
                <Text c="red" size="sm">
                    {t("Error.FetchFillings")}
                </Text>
            )}

            {isSuccess && filtered.length === 0 && (
                <Text c="dimmed" size="sm">
                    {filter.length > 0
                        ? t("Phrase.NoSearchResults")
                        : t("Phrase.NoPreviousFillings")}
                </Text>
            )}

            {isSuccess && filtered.length > 0 && (
                <ScrollArea.Autosize mah={300}>
                    <Stack gap="xs">
                        {filtered.map((c) => (
                            <Anchor
                                key={c.id}
                                component="button"
                                onClick={() =>
                                    handleClick(c.data.id, c.data.name)
                                }
                                style={{
                                    textAlign: "left",
                                    display: "block",
                                }}
                                size="sm"
                            >
                                <Text fw={500} truncate>
                                    {c.data.name}
                                </Text>
                                <Text size="xs" c="dimmed">
                                    {c.data.lastEdit
                                        ? `${t("Phrase.LastEdited")} ${formatDate(c.data.lastEdit)}`
                                        : `${t("Word.Created")} ${formatDate(c.data.created)}`}
                                </Text>
                            </Anchor>
                        ))}
                    </Stack>
                </ScrollArea.Autosize>
            )}

            {!showAll && collections.length > FILLINGS_TO_SHOW && (
                <Button
                    variant="subtle"
                    size="compact-sm"
                    onClick={() => setShowAll(true)}
                >
                    {t("Phrase.ShowMore")}
                </Button>
            )}
        </Stack>
    );
};
